import { ValueTypeSwitcher } from 'legacy/lut/components/widgets/public/partner360/common/valueTypeSwitcher/ValueTypeSwitcher'
import { useCallback, useMemo, useRef, useState } from 'react'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Dropdown } from 'core-components/library/dropdown'
import {
  AbsolutePopup,
  TPopupHorizontal,
  TPopupVertical,
  TVisibilityTrigger,
} from 'core-components/library/popup'
import { Static } from 'core-components/types'
import { Crud } from 'core/types'
import { CurrenciesButtons } from 'partner360/components/currenciesButtons'
import { TOUR_TARGETS } from 'partner360/configs/widgetTour/planningActivitiesTourTargets'
import { TRANSLATION_KEYS } from 'partner360/features/keyInitiatives/configs/translations'
import { KeyInitiativesQuickFilter } from 'partner360/features/keyInitiatives/model/types'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { LIST_GROUP_OPTIONS, LIST_MEASURE_OPTIONS } from '../../config'
import {
  IListFilterNode,
  TListFilterDimension,
  TListFilters,
  TListGroupBy,
  TListMeasure,
} from '../../models'
import { ActiveListFilters, ListFilters } from '../listFilters/ListFilters'

import styles from './ListToolbar.module.scss'

interface IListToolbarProps {
  groupBy: TListGroupBy
  measure: TListMeasure
  onGroupByChange: (value: TListGroupBy) => void
  onMeasureChange: (value: TListMeasure) => void
  actualsType: ActualsValueTypes
  actualsTypes: ActualsValueTypes[]
  onChangeActualsType: (value: ActualsValueTypes) => void
  filters: TListFilters
  options: Record<TListFilterDimension, IListFilterNode[]>
  onFilterChange: (dimension: TListFilterDimension, values: string[]) => void
  onClearFilters: () => void
  onExport: () => void | Promise<void>
  visibleCount: number
  totalCount: number
  isRollupMode: boolean
  quickFilter?: KeyInitiativesQuickFilter | null
  onResetQuickFilter: () => void
}

export const ListToolbar = ({
  groupBy,
  measure,
  onGroupByChange,
  onMeasureChange,
  actualsType,
  actualsTypes,
  onChangeActualsType,
  filters,
  options,
  onFilterChange,
  onClearFilters,
  onExport,
  visibleCount,
  totalCount,
  isRollupMode,
  quickFilter,
  onResetQuickFilter,
}: IListToolbarProps) => {
  const t = useSafetyTranslation()
  const [filtersOpen, setFiltersOpen] = useState(false)
  const filtersPopup = useRef<{ open: () => void; close: () => void }>(null)
  const changeFiltersVisibility = useCallback(
    (visible: boolean) => {
      const allowed = Boolean(visible && !quickFilter)
      setFiltersOpen(allowed)
      return allowed
    },
    [quickFilter]
  )
  const groupSource = useMemo(
    () =>
      new Crud({
        fetcher: new Static(
          LIST_GROUP_OPTIONS.map((option) => ({ ...option, caption: t(option.label) }))
        ),
        keyField: 'key',
      }),
    [t]
  )
  const measureSource = useMemo(
    () =>
      new Crud({
        fetcher: new Static(
          LIST_MEASURE_OPTIONS.map((option) => ({ ...option, caption: t(option.label) }))
        ),
        keyField: 'key',
      }),
    [t]
  )
  const filterCount = Object.values(filters).reduce((count, values) => count + values.length, 0)
  const quickFilterKey = quickFilter ? TRANSLATION_KEYS.quality.quickFilter[quickFilter] : null
  return (
    <div className={styles.root}>
      {quickFilterKey && (
        <div className={styles.quickFilter}>
          <Translation
            text={quickFilterKey}
            replaceParams={[{ key: '{count}', value: String(visibleCount) }]}
          />
          <Button variant="text" onClick={onResetQuickFilter}>
            <Translation text={TRANSLATION_KEYS.dashboard.filters.resetFilters} />
          </Button>
        </div>
      )}
      <div className={styles.toolbar} data-tour-id={TOUR_TARGETS.filters}>
        <div className={styles.groupField}>
          <div className={styles.label}>
            <Translation text={TRANSLATION_KEYS.dashboard.controls.groupBy} />
          </div>
          <Dropdown
            source={groupSource}
            value={groupBy}
            caption="caption"
            disabledInput
            renderFunction={(option: { label: string }) => <Translation text={option.label} />}
            onChange={(value) => {
              const option = LIST_GROUP_OPTIONS.find((item) => item.key === value)
              if (option) onGroupByChange(option.key)
            }}
          />
        </div>
        <div className={styles.measureField}>
          <div className={styles.label}>
            <Translation text={TRANSLATION_KEYS.dashboard.controls.chartMeasure} />
          </div>
          <Dropdown
            source={measureSource}
            value={measure}
            caption="caption"
            disabledInput
            renderFunction={(option: { label: string }) => <Translation text={option.label} />}
            onChange={(value) => {
              const option = LIST_MEASURE_OPTIONS.find((item) => item.key === value)
              if (option) onMeasureChange(option.key)
            }}
          />
        </div>
        <div className={styles.field}>
          <div className={styles.label}>
            <Translation text={TRANSLATION_KEYS.dashboard.controls.currency} />
          </div>
          <CurrenciesButtons size="small" />
        </div>
        <div className={styles.field}>
          <div className={styles.label}>
            <Translation text={TRANSLATION_KEYS.dashboard.controls.unitsMeasure} />
          </div>
          <ValueTypeSwitcher
            size="small"
            valueType={actualsType}
            valueTypes={actualsTypes}
            onChangeValueType={onChangeActualsType}
          />
        </div>
        <div className={styles.filterActions}>
          <AbsolutePopup
            ref={filtersPopup}
            horizontalDirection={TPopupHorizontal.Left}
            horizontalStartPosition={TPopupHorizontal.Right}
            verticalDirection={TPopupVertical.Bottom}
            verticalStartPosition={TPopupVertical.Bottom}
            visibilityTrigger={TVisibilityTrigger.Click}
            visibilityCallback={changeFiltersVisibility}
            popupContent={() => (
              <ListFilters
                filters={filters}
                options={options}
                onChange={onFilterChange}
                onClear={onClearFilters}
                visibleCount={visibleCount}
                totalCount={totalCount}
                isRollupMode={isRollupMode}
              />
            )}
          >
            <button
              type="button"
              className={styles.filterButton}
              disabled={Boolean(quickFilter)}
              aria-expanded={filtersOpen}
              aria-haspopup="dialog"
              onClick={(event) => {
                // The standard popup opens on mousedown; native keyboard clicks need this path.
                if (event.detail === 0 && !quickFilter) {
                  filtersOpen ? filtersPopup.current?.close() : filtersPopup.current?.open()
                }
              }}
            >
              <i className="kbase kbase-filter" aria-hidden="true" />
              <Translation text={TRANSLATION_KEYS.dashboard.filters.title} span />
              {filterCount > 0 && <span className={styles.badge}>{filterCount}</span>}
              <span className={styles.chevron} aria-hidden="true" />
            </button>
          </AbsolutePopup>
          <Button
            variant="outlined"
            color="grey"
            className={styles.exportButton}
            icon="kbase kbase-download"
            onClick={onExport}
          >
            <Translation text={TRANSLATION_KEYS.dashboard.table.export} />
          </Button>
        </div>
      </div>
      <ActiveListFilters
        filters={filters}
        options={options}
        onChange={onFilterChange}
        onClear={onClearFilters}
        disabled={Boolean(quickFilter)}
      />
    </div>
  )
}
